import { useRef } from 'react';
import { List, Box, Drawer, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMenu } from '../hooks/useMenu';
import { theme } from '@theguarantors/tg-package-material-ui';
import { SIDEBAR } from '../constants';

import { MenuContent } from './MenuContent';
import { MenuExpansionControl } from './MenuExpansionControl';
import { Transition } from 'react-transition-group';

export const SideMenu = () => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  // On mobile (drawer mode), don't restore persisted open state on page load
  const { menuOpen, openMenu, closeMenu } = useMenu({ persistOpen: !isMobile });
  const nodeRef = useRef<HTMLDivElement>(null);

  const baseBackgroundColor = theme.palette.coolGrey[50] ?? '#F6F6F7';
  const borderColor = theme.palette.neutralGrey[200];

  // Shared menu content
  const menuContent = (
    <List
      sx={{
        backgroundColor: baseBackgroundColor,
        color: 'white',
        zIndex: 10,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <MenuExpansionControl
        menuOpen={menuOpen}
        openMenu={openMenu}
        closeMenu={closeMenu}
        isMobile={isMobile}
      />

      <MenuContent
        menuOpen={menuOpen}
        persistSectionLabels={isMobile}
        closeMenu={isMobile ? closeMenu : undefined}
      />
    </List>
  );

  // Mobile: Drawer that slides in/out
  if (isMobile) {
    return (
      <Drawer
        transitionDuration={300}
        variant="temporary"
        open={menuOpen}
        anchor="left"
        onClose={closeMenu}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: `${SIDEBAR.width.expanded}px`,
            backgroundColor: baseBackgroundColor,
            borderRight: `1px solid ${borderColor}`,
            borderRadius: '0',
          },
        }}
      >
        {menuContent}
      </Drawer>
    );
  }

  // Desktop: Always-visible sidebar that expands/collapses
  return (
    <Transition in={menuOpen} timeout={10} nodeRef={nodeRef}>
      {(state) => (
        <Box
          ref={nodeRef}
          id="sideMenu"
          sx={{
            height: '100vh',
            position: 'sticky',
            top: 0,
            display: 'flex',
            flexShrink: 0,
            zIndex: 1000,
            transition: `
              width ${SIDEBAR.transition.durationMs} ${SIDEBAR.transition.easing}, 
              min-width ${SIDEBAR.transition.durationMs} ${SIDEBAR.transition.easing}
            `,
            width:
              state === 'entered' ? `${SIDEBAR.width.expanded}px` : `${SIDEBAR.width.collapsed}px`,
            minWidth:
              state === 'entered' ? `${SIDEBAR.width.expanded}px` : `${SIDEBAR.width.collapsed}px`,
            borderRight: `1px solid ${borderColor}`,
            backgroundColor: baseBackgroundColor,
            left: '0px !important',
          }}
        >
          {menuContent}
        </Box>
      )}
    </Transition>
  );
};
